import { allRewards, rewardDic } from "../../../data/rewards";
import {
  entityRecord,
  isHardMode,
  IStats,
  PLAYER_FRAME,
  STARTING_COLS,
  TILE_WIDTH,
} from "../../../data/settings";
import { Container } from "../../../types";
import { shuffle } from "../../../utils/array";
import { styleCoinStat, stylePlayerStat, styleStat } from "../../../utils/parser";
import { descriptionStyle } from "./textHelper";

const SCALE_FACTOR = 1 / 2;

export const toGrid = (n: number) => (n + 0.5) * TILE_WIDTH;
export const fromGrid = (n: number) => Math.round(n / TILE_WIDTH - 0.5);
export const alignToGrid = (n: number) => (n + 0.5) * TILE_WIDTH;

type Pointer = Phaser.Input.Pointer;
type IPoint = [number, number];

const directions = {
  topLeft: [-40 / SCALE_FACTOR, -60 / SCALE_FACTOR],
  topRight: [40 / SCALE_FACTOR, -60 / SCALE_FACTOR],
  bottomRight: [40 / SCALE_FACTOR, 60 / SCALE_FACTOR],
} as const;
type IDirection = keyof typeof directions;

function addStatText(
  scene: Phaser.Scene,
  key: keyof IStats,
  stat: number | [number, number],
  direction: IDirection = "topLeft"
) {
  const [x, y] = directions[direction];
  const label = typeof stat === "number" ? styleStat(stat) : stylePlayerStat(stat);
  const text = scene.add
    .text(x, y, label)
    .setStroke("#2b2b2b", 10)
    .setFontFamily("'Oswald', sans-serif")
    .setName(key)
    .setScale(2)
    .setVisible(stat !== 0);

  // coin stat is also in the form [value,max]
  // we wish to only display its value, and hide it if the value is zero
  if (key === "coin" && typeof stat !== "number") {
    text.setText(styleCoinStat(stat)).setVisible(stat[0] !== 0);
  }

  if (direction === "topLeft") text.setOrigin(0);
  else if (direction === "bottomRight") text.setOrigin(1);
  else if (direction === "topRight") text.setOrigin(1, 0);

  return text;
}

// eslint-disable-next-line complexity
export function addCard(
  scene: Phaser.Scene,
  name: string,
  x = 0,
  y = 0,
  { hp, mp, coin, affinity } = entityRecord[name]
) {
  const bg = scene.add.sprite(0, 0, "atlas", "card_background").setName("bg");
  const border = scene.add.sprite(0, 0, "atlas", "card_border");
  const sprite = scene.add.sprite(0, 0, "atlas", name);

  const isPlayer = name === PLAYER_FRAME;
  // names are typically in the form "creatures/plant"
  const [type, subtype = ""] = name.split("s/");
  if (type === "reward") {
    border.setTintFill(0xffffff);
    bg.setTintFill(0x101010);
  }
  if (type === "item") border.setTintFill(0xe76e55);
  if (isPlayer) {
    border.setTintFill(0xffd700);
    sprite.setScale(2);
  }
  // else if (affinity === "wind") border.setTintFill(0x92cc41);
  // else if (affinity === "water") border.setTintFill(0x209cee);
  // else if (affinity === "fire") border.setTintFill(0xe76e55);

  const card = scene.add
    .container(toGrid(x), toGrid(y), [bg, border, sprite])
    .setSize(sprite.width, sprite.height)
    .setScale(SCALE_FACTOR);

  // for performance reasons, we add the text later (and tween it to make it look cool)
  setTimeout(() => {
    const hpText = addStatText(scene, "hp", hp).setFill("#e76e55");
    const mpText = addStatText(scene, "mp", mp, "bottomRight").setFill("#209cee");
    const coinText = addStatText(scene, "coin", coin ?? 0, "topRight").setFill("#ffd700");

    card.add([hpText, mpText, coinText]);
    scene.tweens.add({
      targets: [hpText, mpText, coinText],
      alpha: { from: 0, to: 1 },
      ease: "Expo",
    });
  }, 300);

  card.setName(`${x},${y}`);
  card.setData({ hp, mp, coin, type, subtype });

  return card;
}

function getCols(level: number) {
  return level * 2 + STARTING_COLS;
}

const rows = [0, 1, 2];
export function addRewards(scene: Phaser.Scene, level: number) {
  const potentialRewards = allRewards
    .slice(1)
    .filter((reward) => level >= rewardDic[reward].minLevel);
  let availableRewards = shuffle(potentialRewards);
  // middle reward should always help newbies
  // eslint-disable-next-line prefer-destructuring
  if (!isHardMode) availableRewards[1] = allRewards[0];
  const x = getCols(level) + 1;
  rows.forEach((_, i) => {
    if (availableRewards[i] === undefined) availableRewards = shuffle(potentialRewards);
    const reward = availableRewards[i] ?? allRewards[0];
    addCard(scene, reward, x, i);
    const { description } = rewardDic[reward] ?? { description: "..." };
    scene.add.text(toGrid(x + 1), toGrid(i), description, descriptionStyle).setDepth(999);
  });
}

const hitArea = new Phaser.Geom.Rectangle(
  -TILE_WIDTH / 2,
  -TILE_WIDTH / 2,
  TILE_WIDTH * 1.5,
  TILE_WIDTH * 2
);
const hitAreaCallback = Phaser.Geom.Rectangle.Contains;
const dragConfig = { useHandCursor: true, hitArea, draggable: true, hitAreaCallback };

export function setupDrag(
  scene: Phaser.Scene,
  card: Container,
  callback: (gameObject: Container, next: IPoint, initial: IPoint, gridPoint: IPoint) => void
) {
  card.setInteractive(dragConfig);

  let [initialX, initialY] = [card.x, card.y];

  scene.input.on("dragstart", () => {
    [initialX, initialY] = [card.x, card.y];
  });

  scene.input.on(
    "drag",
    (pointer: Pointer, gameObject: Container, dragX: number, dragY: number) => {
      gameObject.x = dragX;
      gameObject.y = dragY;
    }
  );

  scene.input.on("dragend", (pointer: Pointer, gameObject: Container) => {
    const [gridX, gridY] = [fromGrid(gameObject.x), fromGrid(gameObject.y)];
    const [aX, aY] = [alignToGrid(gridX), alignToGrid(gridY)];

    return callback(gameObject, [aX, aY], [initialX, initialY], [gridX, gridY]);
  });
  return card;
}
